import Cookies from 'js-cookie';

import { fill, find, includes, keys, map } from 'lodash';
import { supportedLocales, SupportedLocales } from '../i18n/supported-locales';

import {
    AUDIOKINETIC_DOT_COM,
    COOKIE_USER_LOCALE,
    LOCAL_SERVER_REPLACE_LOOKUP,
    SUPPORTED_OS,
} from '../constants';
import { isDev, isProd, isJakhub, isJakhubForChina } from './build-utils';

export { isJakhub, isDev, isProd, isJakhubForChina };

export const cookieLanguage = () => {
    return Cookies.get(COOKIE_USER_LOCALE);
};

type PartialWindowLocation = {
    protocol: string;
    host: string;
};
export function backendHostNameForBlog(
    hostLocation: PartialWindowLocation,
    includeProtocol = true
) {
    const { protocol, host } = hostLocation;

    const protocolPrefix = includeProtocol ? `${protocol}//` : '';

    if (host.indexOf('audiokinetic.com') !== -1) {
        return host.indexOf('blog') !== -1
            ? `${protocolPrefix}${host.replace(/blog/g, 'www')}`
            : '';
    }

    if (host.indexOf('gowwise.com') !== -1) {
        return host.indexOf('blog-') !== -1 ? `${protocolPrefix}${host.replace(/blog-/g, '')}` : '';
    }

    return '';
}

export const extractDiff = (objLeft: Record<string, any>, objRight: Record<string, any>) => {
    const diffs = {
        leftIsMissing: {},
        rightIsMissing: {},
        valuesAreDifferent: {},
    } as Record<string, Record<string, string | Record<string, string>>>;

    const keysLeft = keys(objLeft);
    const keysRight = keys(objRight);

    keysLeft.forEach((k) => {
        if (!includes(keysRight, k)) {
            diffs.rightIsMissing[k] = objLeft[k];
        }

        if (includes(keysRight, k) && objLeft[k] !== objRight[k]) {
            diffs.valuesAreDifferent[k] = { l: objLeft[k], r: objRight[k] };
        }
    });

    keysRight.forEach((k) => {
        if (!includes(keysLeft, k)) {
            diffs.leftIsMissing[k] = objRight[k];
        }

        if (includes(keysLeft, k) && objLeft[k] !== objRight[k]) {
            diffs.valuesAreDifferent[k] = { l: objLeft[k], r: objRight[k] };
        }
    });

    return diffs;
};

export const stripHttp = (url: string): string =>
    url.toLowerCase().replace(new RegExp(/^(https?:|)\/\//g), '');

const knownProtocols = ['http', 'mail'];
export const isHttpLink = (path: string): boolean => knownProtocols.includes(path?.substring(0, 4));

export const extractDomainFromUrl = (url: string) => {
    const a = document.createElement('a');
    a.setAttribute('href', url);
    return a.hostname;
};
export const isStaticImport = (path: string): boolean =>
    path.indexOf('/jakhub/static/media/') !== -1;

export const isNonHttpProtocol = (path: string): boolean =>
    path?.indexOf('://') !== -1 && !isHttpLink(path);

export const isNonAkLink = (path = ''): boolean => {
    // To be a non AK link it has to be fully qualified
    const isAnExplicitLink = isHttpLink(path);

    // If it's a media.gowwise link we need a new window
    const isAnExternalMediaLink = path.indexOf('media.gowwise.') !== -1;

    // audiokinetic AND gowwise should be considered internal NO-NEW-TAB links
    const isAnAkLink = path.indexOf('.audiokinetic.') === -1 && path.indexOf('.gowwise') === -1;

    return isAnExplicitLink && (isAnExternalMediaLink || isAnAkLink);
};

export const isExternalLink = (path = ''): boolean => {
    if (path.slice(-3).toLowerCase() === 'pdf') return true;

    if (!isHttpLink(path)) return false;

    if (path.indexOf('media.gowwise') !== -1) return true;
    if (path.indexOf('://info.audiokinetic') !== -1) return true;

    return !(path.indexOf('.audiokinetic.') !== -1 || path.indexOf('gowwise') !== -1);
};

export const injectLanguageSlug = (
    path: string,
    slug: string,
    includeLanguageSlug: boolean
): string => {
    const pathIsExplicit = path.indexOf('://') !== -1;

    if (!includeLanguageSlug) return path;

    if (isNonAkLink(path) || isExternalLink(path)) return path;
    if (!pathIsExplicit) return `/${slug}${path}`;

    const [protocol, fullPath] = path.split('://');
    const [domain, ...rest] = fullPath.split('/');
    return `${protocol}://${[domain, slug].concat(...rest).join('/')}`;
};

export const protocolHostnamePath = (contextWindow = window): string => {
    const {
        location: { host, protocol },
    } = contextWindow;

    const triggerString = host?.indexOf('audiokinetic.com') !== -1 ? 'blog' : 'blog-';
    const targetString = host?.indexOf('audiokinetic.com') !== -1 ? 'www' : '';

    const targetHostName = host?.replace(triggerString, targetString);

    return `${protocol}//${targetHostName}`;
};

export const getHardPathTarget = (
    contextWindow: Window | any = typeof window !== 'undefined' ? window : global
): string => {
    return isDev() || isJakhubForChina()
        ? AUDIOKINETIC_DOT_COM
        : protocolHostnamePath(contextWindow);
};

export const getFallbackBackendServer = (
    contextWindow: Window | any = typeof window !== 'undefined' ? window : global
): string => {
    const {
        location: { host, protocol },
    } = contextWindow;

    return !isDev()
        ? `${protocol}//${host}`
        : LOCAL_SERVER_REPLACE_LOOKUP[host as string] || `${protocol}//${host}`;
};

export function getCurrentPlatform(): 'linux' | 'windows' | 'mac' {
    if (navigator.userAgent.includes('Win')) {
        return 'windows';
    }
    if (navigator.userAgent.includes('Mac')) {
        return 'mac';
    }
    if (navigator.userAgent.includes('Linux')) {
        return 'linux';
    }
    // fallback for unknown OS
    return 'windows';
}

export const balanceArrayLines = (
    inputArray: Array<unknown>,
    maxItemsPerLine = 9
): Array<number> => {
    /*
    1-9 -> 1-9
    10 -> 5 + 5
    11 -> 6 + 5
    12 -> 6 + 6
    13 -> 7 + 6
    14 -> 7 + 7
    15 -> 8 + 7
    16 -> 8 + 8
    17 -> 9 + 8
    18 -> 9 + 9
    19 -> 7 + 6 + 6
    20 -> 7 + 7 + 6
    21 -> 7 + 7 + 7
    22 -> 7 + 7 + 7
    23 -> 8 + 7 + 7
    24 -> 8 + 8 + 8
    25 -> 9 + 8 + 8
    26 -> 9 + 9 + 8
    27 -> 9 + 9 + 9
    28 -> 7 + 7 + 7 + 7
    29 -> 8 + 7 + 7 + 7
    30 -> 8 + 8 + 7 + 7
    31 -> 8 + 8 + 8 + 7
    32 -> 8 + 8 + 8 + 8
    33 -> 9 + 8 + 8 + 8
    34 -> 9 + 9 + 8 + 8
    35 -> 9 + 9 + 9 + 8
    36 -> 9 + 9 + 9 + 9
    37 -> 8 + 8 + 7 + 7 + 7
    38 -> 8 + 8 + 8 + 7 + 7
    39 -> 8 + 8 + 8 + 8 + 7
    40 -> 8 + 8 + 8 + 8 + 8
    41 -> 9 + 8 + 8 + 8 + 8
    42 -> 9 + 9 + 8 + 8 + 8
    43 -> 9 + 9 + 9 + 8 + 8
    44 -> 9 + 9 + 9 + 9 + 8
    45 -> 9 + 9 + 9 + 9 + 9
    46 -> 8 + 8 + 8 + 8 + 7 + 7
    47 -> 8 + 8 + 8 + 8 + 8 + 7
    48 -> 8 + 8 + 8 + 8 + 8 + 8
    49 -> 9 + 8 + 8 + 8 + 8 + 8
    50 -> 9 + 9 + 8 + 8 + 8 + 8
 */
    const numberOfLines = Math.ceil(inputArray.length / maxItemsPerLine);
    const minItemsPerLine = Math.floor(inputArray.length / numberOfLines);
    const arrayConfig = fill(Array(numberOfLines), minItemsPerLine);
    let distribute = inputArray.length - numberOfLines * minItemsPerLine;
    let listIndex = 0;
    while (distribute) {
        arrayConfig[listIndex++]++;
        distribute--;
    }
    return arrayConfig;
};

export const capitalize = (input = ''): string => input.charAt(0).toUpperCase() + input.slice(1);
export const uncapitalize = (input = ''): string => input.charAt(0).toLowerCase() + input.slice(1);

export const toCamelCase = (input = ''): string => {
    let rep = input;
    rep = rep.replace(new RegExp(/&/g), '');
    rep = rep
        .split(' ')
        .map((word) => capitalize(word))
        .join('');
    rep = uncapitalize(rep);
    return rep;
};

export const localeLookup: Record<SupportedLocales, string> = {
    [SupportedLocales.EN_US]: 'en_US',
    [SupportedLocales.FR_CA]: 'fr_FR',
    [SupportedLocales.JA_JP]: 'ja_JP',
    [SupportedLocales.KO_KR]: 'ko_KR',
    [SupportedLocales.ZH_CN]: 'zh_CN',
};

export const languageToLocale = (language: string) =>
    find(SupportedLocales, (supLang) => supLang.substr(0, 2) === language) || supportedLocales[0];

export const localeToLanguage = (locale: string): string =>
    locale.split(new RegExp('[-_]', 'g'))[0];

export const removeDuplicates = (arr: string[]) => [...new Set(arr)];

export const sanitizeAndCapitalizeLanguages = (languages: string[]) => {
    return removeDuplicates(languages?.map((l: string) => capitalize(l.toLowerCase())));
};

export const snakeCase = (input = ''): string => input.replace(/-/g, '_');
export const upperSnakeCase = (input = ''): string => snakeCase(input).toUpperCase();
export const lowerSnakeCase = (input = ''): string =>
    input
        .replace(/[A-Z]/g, (letter, index) => `${index !== 0 ? '_' : ''}${letter.toLowerCase()}`)
        .replace(/- /g, '_')
        .toLowerCase();
export const stripNonAlphaNumeric = (input = ''): string => input.replace(/[\W_]+/g, '');

export const noSpaceDash = (str: string): string => str.replace(new RegExp(/[\s-]/g), '');

// eslint-disable-next-line max-statements
export const accentsTidy = (str: string): string => {
    let rep = str;
    rep = rep.replace(new RegExp(/[àáâãäå]/g), 'a');
    rep = rep.replace(new RegExp(/[ÀÁÂÃÄÅ]/g), 'A');
    rep = rep.replace(new RegExp(/[èéêë]/g), 'e');
    rep = rep.replace(new RegExp(/[ÈÉÊË]/g), 'E');
    rep = rep.replace(new RegExp(/[ìíîï]/g), 'i');
    rep = rep.replace(new RegExp(/[ÌÍÎÏ]/g), 'I');
    rep = rep.replace(new RegExp(/[òóôõö]/g), 'o');
    rep = rep.replace(new RegExp(/[ÒÓÔÕÖ]/g), 'O');
    rep = rep.replace(new RegExp(/[ùúûü]/g), 'u');
    rep = rep.replace(new RegExp(/[ÙÚÛÜ]/g), 'U');
    rep = rep.replace(new RegExp(/[ýÿ]/g), 'y');
    rep = rep.replace(new RegExp(/[ÝŸ]/g), 'Y');
    rep = rep.replace(new RegExp(/æ/g), 'ae');
    rep = rep.replace(new RegExp(/Æ/g), 'AE');
    rep = rep.replace(new RegExp(/ç/g), 'c');
    rep = rep.replace(new RegExp(/Ç/g), 'C');
    rep = rep.replace(new RegExp(/ñ/g), 'n');
    rep = rep.replace(new RegExp(/Ñ/g), 'N');
    rep = rep.replace(new RegExp(/œ/g), 'oe');
    rep = rep.replace(new RegExp(/Œ/g), 'OE');
    return rep;
};

export const extractEnvName = (hostname: string): string | null => {
    const hostnameSplit = hostname.split('.');
    if (hostnameSplit.length !== 3) {
        return null;
    }
    const subDomainSplit = hostnameSplit[0].split('-');
    if (subDomainSplit.length > 1) return subDomainSplit[1];
    return 'prod';
};

export const isValidEmail = (email: string): Boolean =>
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()\\[\].,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
        String(email).toLowerCase()
    );

export const stringToDate = (dateString: string): Date => {
    const dateArray = dateString.split('-').map((piece) => parseInt(piece, 10));
    return new Date(dateArray[0], dateArray[1] - 1, dateArray[2]);
};

export function randomInteger(min: number, max: number) {
    return Math.floor(Math.random() * (max - min + 1)) + min;
}

export const dateToString = (date: Date): string => {
    return [
        date.getUTCFullYear(),
        `${date.getUTCMonth() + 1}`.padStart(2, '0'),
        `${date.getUTCDate()}`.padStart(2, '0'),
    ].join('-');
};

export const timeToString = (time: Date): string => {
    return [
        time.getUTCHours(),
        `${time.getUTCMinutes()}`.padStart(2, '0'),
        `${time.getUTCSeconds()}`.padStart(2, '0'),
    ].join(':');
};

export const extractFromDateTime = (dateTime: any) => {
    const newDate = new Date(dateTime || 0);

    const date = newDate.toISOString()?.split('T')[0];
    const time = newDate.toTimeString().substring(0, 8);

    return { date, time };
};

export const absoluteDate = (dateString: string) => {
    const dateArray = dateString.split('-').map((piece) => parseInt(piece, 10));
    const date = new Date(dateArray[0], dateArray[1] - 1, dateArray[2]);
    const userTimezoneOffset = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() + userTimezoneOffset);
};

export const localeDate = (dateString: string, userLocale: SupportedLocales | 'en-CA') =>
    Intl.DateTimeFormat(userLocale).format(new Date(dateString));

export const shuffleArray = (array: any[]): any[] => {
    const mappedArray = map(array, (i) => i);
    let m = mappedArray.length,
        t,
        i;

    while (m) {
        i = Math.floor(Math.random() * m--);
        t = mappedArray[m];
        mappedArray[m] = mappedArray[i];
        mappedArray[i] = t;
    }

    return mappedArray;
};

export const setValueWithPath = (obj: any, path: string, value: any) => {
    const a = path.split('.');
    let o = obj;

    while (a.length - 1) {
        const n = a.shift() || '';
        if (!o?.[n]) o[n] = {};
        o = o[n];
    }

    o[a[0]] = value;
};

export const getValueWithPath = (obj: any, path: string) => {
    if (!obj || !path) {
        return null;
    }
    path = path.replace(/\[(\w+)\]/g, '.$1');
    path = path.replace(/^\./, '');
    const a = path.split('.');
    let o = obj;
    while (a.length) {
        const n = a.shift() || '';
        if (o?.[n] === undefined) return;
        o = o[n];
    }
    return o;
};

export const setsAreEqual = (setA: any, setB: any) =>
    setA.size === setB.size && [...setA].every((element) => setB.has(element));

export async function detectOs() {
    const platform = await navigator.userAgentData?.platform;

    if (platform?.indexOf('Win') !== -1) return SUPPORTED_OS.WINDOWS;
    if (platform?.indexOf('Mac') !== -1) return SUPPORTED_OS.MACOS;
    if (platform?.indexOf('Linux') !== -1) return SUPPORTED_OS.LINUX;
    return 'unsuppported_version';
}

const loremIpsumText =
    'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed non ultricies sapien. Praesent at lorem non felis blandit aliquet. Cras ultrices orci sed efficitur blandit. Curabitur in tincidunt nulla, sed varius libero. Etiam consectetur enim dignissim scelerisque condimentum. Morbi sem nulla, egestas et malesuada sit amet, ornare eu sapien. Mauris porttitor lacinia pellentesque. Aliquam rutrum lacinia ex, a rutrum dui aliquet vitae. Aenean egestas scelerisque ullamcorper. Aliquam mattis ultricies nisl, ut accumsan purus congue ut. Ut lectus tellus, egestas id nunc sodales, viverra varius mauris.\n In pulvinar, nisi nec suscipit dapibus, urna magna ultricies ex, eu pellentesque neque tellus quis dui. Sed ac arcu et velit egestas bibendum ac vel eros. Fusce pharetra dapibus leo ut molestie. Vestibulum scelerisque lorem turpis, at accumsan turpis accumsan ac. Nam eget venenatis augue. Pellentesque at cursus libero, vitae tempor ipsum. Sed pulvinar in mauris vel imperdiet. Donec in nunc sodales, congue orci eget, rhoncus enim. Fusce sed laoreet sem, sit amet fermentum erat. In malesuada egestas nibh, at placerat dolor aliquam varius. Ut rutrum a arcu a finibus. Suspendisse ut massa ut velit cursus efficitur vel nec libero. Fusce dictum lacinia ligula. Curabitur nec malesuada velit, vel egestas dui. Etiam scelerisque, lorem quis rutrum viverra, purus lectus ultrices orci, non dignissim nisi elit et arcu. Interdum et malesuada fames ac ante ipsum primis in faucibus.\n\nDuis tincidunt vulputate felis, in scelerisque nibh interdum consectetur. Vestibulum vel fermentum ante. Donec felis ante, placerat vitae auctor in, suscipit eu felis. Nam sollicitudin hendrerit eros, nec tempus arcu suscipit at. Cras at dignissim metus, in bibendum nunc. Phasellus a arcu ac purus porttitor ornare a condimentum leo. Donec dignissim feugiat ornare. Maecenas sem urna, pretium eget erat ut, mollis dignissim metus.\n\nMauris dapibus in nisl ac ultricies. Nunc sapien velit, aliquet ac interdum quis, lacinia eget nisi. Aliquam eu congue tellus. Duis eu magna vitae mi feugiat congue ut posuere orci. Nulla molestie nisi vitae purus ultricies posuere. Proin scelerisque at turpis vitae accumsan. Duis accumsan egestas est vel placerat. Aliquam tempus nisi ipsum, porta condimentum ipsum laoreet in.\n\nSuspendisse sed commodo justo. Suspendisse consequat lorem nulla, sit amet iaculis mi vehicula ac. Maecenas interdum ornare congue. Vivamus scelerisque vel erat quis ultrices. Morbi posuere ut purus et sodales. Cras quis convallis nibh, quis venenatis nibh. Integer pretium laoreet felis, non placerat quam pretium eget. Aenean euismod nibh eu urna commodo maximus. In tempus orci eu facilisis euismod. Maecenas non fringilla arcu, non dapibus ante.\n\nMaecenas auctor pulvinar felis, vel accumsan sapien ullamcorper non. Aenean venenatis elementum ex, ac commodo nisl dignissim nec. Vestibulum gravida lectus quis odio convallis fringilla. Nam felis elit, tristique ac tincidunt sit amet, mollis et quam. Nunc consequat neque ut posuere fermentum. Vestibulum sed orci eget velit hendrerit porttitor ut a enim. Phasellus malesuada justo tellus, sit amet eleifend enim molestie sed. Nam vulputate eros quam, eget venenatis ex fringilla ac. Suspendisse libero eros, mattis eu iaculis vitae, ornare sed augue. Cras consequat interdum turpis, eu mattis eros dictum eu. Ut vehicula nisi non efficitur gravida.\n\nCurabitur porta bibendum mauris dictum pretium. Nullam tristique, eros nec scelerisque dictum, mi quam bibendum urna, sed eleifend enim mauris sollicitudin nibh. Nulla elementum nunc et massa mollis ullamcorper. Quisque maximus vel quam nec eleifend. Praesent vitae venenatis nunc, gravida consequat elit. Pellentesque vel massa metus. Class aptent taciti sociosqu ad litora torquent per conubia nostra, per inceptos himenaeos. Suspendisse tempus ullamcorper dui nec aliquet. Nunc nibh metus, egestas vitae blandit et, auctor in ipsum. Aenean non lacus non dui semper tempor ac eu mi. Mauris a risus quis ipsum euismod ultrices ut ac magna. In vel arcu vel ipsum venenatis interdum laoreet ullamcorper diam. Duis eu ullamcorper dui. Morbi rutrum pharetra odio, auctor tempor mi fringilla nec. Donec in sem nec mi semper efficitur ut at quam. Maecenas accumsan, enim et varius egestas, dui quam ornare nisl, viverra facilisis nunc est nec tortor.\n\nAenean sollicitudin et arcu sed lacinia. Nullam risus nisl, porttitor eget neque et, maximus vehicula lacus. Morbi imperdiet et nibh vel varius. Pellentesque molestie gravida erat, a placerat felis facilisis in. Curabitur vel dolor pellentesque, elementum sapien in, auctor velit. Maecenas vel sem eu lacus condimentum efficitur. Sed aliquam hendrerit cursus. Nullam vulputate tellus nulla. Fusce quis sagittis velit. Nam consectetur tellus in erat accumsan, id sodales ex gravida.\n\nAliquam posuere nulla ac lobortis imperdiet. Nulla quis sollicitudin elit, vel hendrerit urna. Nulla vehicula urna ante, sed rhoncus neque rhoncus vel. Maecenas urna odio, rutrum rhoncus augue in, molestie vehicula tellus. Donec at urna et sapien laoreet dignissim. Mauris tempus, lorem at egestas malesuada, leo arcu consectetur sapien, nec lobortis nibh ligula vel ligula. Mauris accumsan neque sit amet sapien varius dictum vitae vel neque. Suspendisse sit amet tristique ligula, in tincidunt magna. Suspendisse potenti. Proin id lectus id justo mollis tempor ut ac neque.\n\nCurabitur eget felis id velit rutrum dapibus a quis mauris. Donec lacinia mi ut luctus lobortis. Nulla mattis, arcu nec tincidunt vulputate, nibh tellus mollis quam, quis feugiat metus velit eget diam. Sed a pretium dui, a feugiat libero. Duis faucibus ligula sit amet tellus scelerisque laoreet. Praesent tincidunt a dolor vel egestas. Suspendisse non enim vel urna posuere posuere et non lectus. Pellentesque pulvinar elementum convallis. Phasellus lacus odio, ornare eget massa et, porta semper dolor. Mauris convallis urna ut fringilla auctor. Morbi dolor enim, sodales quis sem ut, accumsan bibendum metus. Pellentesque commodo diam sit amet tortor aliquam vestibulum. Nunc rutrum tempor magna, ut luctus urna. Nullam luctus nunc eu arcu posuere bibendum.\n\nNulla facilisi. Sed efficitur accumsan risus nec interdum. Sed sit amet magna at dui commodo luctus. Suspendisse commodo eros tellus, mollis mattis libero fringilla id. Aenean vitae felis eu mauris consequat sagittis vitae eget libero. Nam quis lacus rutrum, fringilla nunc ac, laoreet velit. Praesent nec venenatis lacus, eget dapibus elit. Integer ac neque ante. Aenean viverra suscipit condimentum. Phasellus eget nisi velit. Praesent maximus cursus libero quis molestie.\n\nCras scelerisque augue ut mauris sollicitudin consequat. Fusce scelerisque posuere leo, sit amet pharetra nunc sodales scelerisque. Praesent viverra lorem a urna sodales pharetra. Pellentesque pellentesque faucibus metus quis rutrum. Sed suscipit mollis porttitor. Maecenas ultrices velit in tempus semper. Sed egestas arcu pulvinar bibendum tincidunt. Morbi ac tellus a nisi malesuada auctor non vitae lorem. Donec finibus neque leo, eget hendrerit tellus cursus sit amet. Aliquam ut ipsum vel justo luctus cursus. Integer accumsan arcu metus, vitae vehicula dui pharetra sed. Praesent eu odio velit. Integer rutrum nisl sed posuere maximus.\n\nClass aptent taciti sociosqu ad litora torquent per conubia nostra, per inceptos himenaeos. Class aptent taciti sociosqu ad litora torquent per conubia nostra, per inceptos himenaeos. Maecenas vulputate ante a pellentesque pharetra. Aenean tempus nulla sed diam bibendum laoreet. Pellentesque lorem ligula, blandit sed mi eu, mattis vehicula mi. Phasellus fermentum orci sed consectetur sodales. In auctor gravida nibh, et rhoncus metus tempus quis. Ut placerat dolor sapien, vel porta tellus aliquam nec. Donec pulvinar ligula dolor, vel sollicitudin elit vulputate vel. Donec egestas risus vel porta fringilla. Mauris condimentum sapien non nisi volutpat, in imperdiet ex dignissim.\n\nNullam iaculis facilisis libero vel finibus. Praesent ac lorem nec augue maximus hendrerit. Vivamus eget est at tortor varius finibus nec imperdiet odio. Integer vehicula consequat sem, nec finibus magna porta vitae. Donec nisl massa, lacinia sit amet iaculis laoreet, tempor id massa. In nec interdum felis. Nunc vitae malesuada mauris. Etiam dui mauris, tincidunt in dolor vulputate, fringilla malesuada sem. Ut non tortor augue.\n\nQuisque bibendum est ut tortor posuere consequat. Etiam sollicitudin massa quam, eget iaculis quam hendrerit quis. In non lectus viverra, malesuada odio non, fermentum magna. Pellentesque id est urna. Phasellus commodo gravida lacus, euismod faucibus leo. Etiam sit amet dapibus elit. Pellentesque habitant morbi tristique senectus et netus et malesuada fames ac turpis egestas. Orci varius natoque penatibus et magnis dis parturient montes, nascetur ridiculus mus. Vestibulum in interdum arcu. Phasellus sit amet sagittis massa, et eleifend nunc. Praesent dapibus varius enim, finibus faucibus nibh euismod eu.\n\nFusce dapibus justo ac lectus sollicitudin, ultricies maximus quam condimentum. Maecenas ultrices nulla libero, eget molestie ligula blandit in. Aenean nisl ante, hendrerit quis eros ut, euismod feugiat diam. Vivamus interdum mi id facilisis elementum. Nullam non sem libero. Vivamus ac nibh luctus, eleifend neque ut, fringilla orci. Curabitur varius ipsum metus, varius hendrerit mi egestas in. Aenean vitae nulla vel nisl euismod vulputate malesuada ac mauris. Duis eget nisi augue. Ut porta lectus non dolor placerat lacinia. Nam finibus, urna sed consectetur convallis, sem eros bibendum turpis, quis consectetur sapien magna non ipsum. Sed purus risus, semper vel sem quis, scelerisque placerat velit. Pellentesque consectetur eros non ante vehicula pellentesque. Maecenas ut tortor eget justo pretium mattis. Vestibulum condimentum, magna a luctus egestas, ex nisi accumsan tellus, sit amet faucibus tortor eros non felis. Donec pulvinar tincidunt metus a pharetra.\n\nCurabitur mollis efficitur arcu et pharetra. Fusce leo velit, pharetra quis volutpat in, hendrerit dapibus neque. Phasellus aliquam suscipit eros ut interdum. Maecenas justo lectus, placerat quis euismod sed, scelerisque vitae elit. Phasellus neque ante, auctor a ultrices quis, egestas non sem. Etiam at tincidunt felis, sed molestie mi. Fusce dignissim et nulla et lobortis.\n\nMauris convallis lorem at ligula elementum venenatis. Sed interdum luctus ipsum sodales sollicitudin. Class aptent taciti sociosqu ad litora torquent per conubia nostra, per inceptos himenaeos. Cras fringilla placerat lobortis. Curabitur nisi leo, sodales hendrerit ultrices vitae, mollis vitae turpis. Lorem ipsum dolor sit amet, consectetur adipiscing elit. Quisque tempor diam eget tempor consequat. Praesent consectetur, eros sed mollis tristique, risus nunc suscipit lectus, eget placerat quam leo et tellus. Duis vestibulum quam non velit ultrices, eu lacinia nunc auctor. Curabitur vestibulum erat non ligula cursus, vel volutpat quam posuere. Class aptent taciti sociosqu ad litora torquent per conubia nostra, per inceptos himenaeos. Donec sed tellus eu leo viverra blandit. Ut eget pretium risus. Sed tincidunt quam accumsan, sodales eros in, blandit elit. Quisque nec ullamcorper sapien. Maecenas a viverra ipsum, id hendrerit elit.\n\nProin faucibus, felis id rhoncus sollicitudin, magna massa pellentesque magna, quis commodo metus est et velit. Donec dignissim sem a lacus viverra, blandit dapibus diam luctus. Etiam in suscipit arcu, vitae bibendum justo. Aliquam lobortis rhoncus turpis in mollis. Orci varius natoque penatibus et magnis dis parturient montes, nascetur ridiculus mus. Nulla porttitor ante eget elit eleifend, in sodales augue convallis. Donec at diam vel ligula faucibus luctus. Donec aliquet ante interdum, sagittis quam nec, faucibus magna. Nunc consectetur magna eget turpis pellentesque aliquet et et ante. Duis ut metus elit. Quisque pharetra efficitur tristique. Aliquam id dictum elit. Vivamus porta, enim non egestas lobortis, diam risus semper tellus, et laoreet risus metus at diam. Etiam id eros fermentum, tempus mi quis, laoreet ipsum.\n\nMauris fermentum rhoncus accumsan. Aenean egestas ultrices nunc, in aliquet felis venenatis a. In vel diam aliquet, rutrum tellus ac, fringilla augue. In nibh erat, laoreet bibendum scelerisque id, varius sed lectus. Cras molestie sagittis ante, quis laoreet velit malesuada non. Nunc eget leo eget nunc ultricies scelerisque. Maecenas volutpat quam nunc, sed iaculis odio mollis sit amet. Aliquam elementum enim vel elit accumsan, non faucibus mauris maximus. Vestibulum ante ipsum primis in faucibus orci luctus et ultrices posuere cubilia curae; Donec id justo sit amet eros sodales porttitor et vitae nunc. Nulla malesuada posuere venenatis. Vestibulum convallis a lacus eu rutrum. Duis pretium justo nec elit maximus, porta semper sapien rutrum. In in condimentum nunc. Ut mollis quis orci sit amet facilisis. Nam quis suscipit mi.\n\nMaecenas quis bibendum tellus, a porttitor libero. Donec eu condimentum mauris. Aliquam aliquet est eget ex imperdiet aliquam. Nunc rhoncus, quam quis malesuada ornare, ipsum tellus aliquet libero, eu faucibus quam metus sed arcu. Donec feugiat odio a libero varius venenatis. Sed hendrerit libero justo, vitae sagittis quam consectetur ac. Integer sed congue lorem. Nulla consectetur tristique massa, at maximus velit facilisis ac. Mauris congue mi ac sapien aliquet, vitae imperdiet est tincidunt. Aliquam rhoncus, dolor et iaculis tincidunt, lectus dolor maximus risus, id dignissim nisl tortor et augue.';
export const loremIpsum = (numberOfWords: number) => {
    const words = shuffleArray(loremIpsumText.split(' '));

    return numberOfWords <= words.length
        ? words.slice(0, numberOfWords).join(' ')
        : words.join(' ');
};
